/**
 * LayoutConfigProvider — Wraps the app to provide DSL config to all children.
 *
 * Fetches DynamicConfig once, then shares it via React Context.
 * All UI components read from this context — zero hardcoded values.
 */

import { type ReactNode } from 'react';
import { useLayoutConfig, LayoutConfigContext } from '@/lib/use-layout-config';

interface LayoutConfigProviderProps {
  children: ReactNode;
}

export function LayoutConfigProvider({ children }: LayoutConfigProviderProps) {
  const { config, loading, entities, refresh } = useLayoutConfig();

  if (loading || !config) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
          <span className="text-sm text-muted-foreground">Loading configuration…</span>
        </div>
      </div>
    );
  }

  return (
    <LayoutConfigContext.Provider value={{ config, entities, refresh }}>
      {children}
    </LayoutConfigContext.Provider>
  );
}
